<template>
  <transition name="uber-modal">
    <div v-if="isOpen" class="uber-modal-overlay" @click.self="handleOverlayClick">
      <div class="uber-modal">
        <!-- Uber Header -->
        <div class="uber-header">
          <div class="uber-logo">
            <img src="/images/Uber_Eats_2020_logo.svg.png" alt="Uber Eats" />
          </div>
          <div class="header-right">
            <div class="header-order-id">#{{ order.uberDisplayId || '7542D' }}</div>
            <button class="call-btn" @click="showCustomerInfo = !showCustomerInfo">
              <i class="fas fa-phone"></i>
            </button>
          </div>
        </div>

        <!-- Customer Info Popup -->
        <div v-if="showCustomerInfo" class="customer-info-popup">
          <div class="info-row">
            <i class="fas fa-user"></i>
            <span>{{ order.customer?.name || 'Test Kunde' }}</span>
          </div>
          <div class="info-row phone" @click="callCustomer">
            <i class="fas fa-phone"></i>
            <span>{{ order.customer?.phone || '+49 123 456789' }}</span>
          </div>
          <div v-if="order.customer?.phoneCode" class="info-row">
            <i class="fas fa-key"></i>
            <span class="pin">PIN: {{ order.customer.phoneCode }}</span>
          </div>
          <div class="info-row">
            <i class="fas fa-map-marker-alt"></i>
            <span>{{ order.deliveryAddress || 'Musterstraße 123, 34117 Kassel' }}</span>
          </div>
          <div v-if="order.deliveryNotes" class="info-row notes">
            <i class="fas fa-sticky-note"></i>
            <span>{{ order.deliveryNotes }}</span>
          </div>
        </div>

        <!-- Items List -->
        <div class="items-section">
          <div class="section-title">
            <i class="fas fa-shopping-bag"></i>
            <span>{{ itemCount }} Artikel</span>
          </div>
          <div class="items-list">
            <div v-for="(item, index) in order.items || mockItems" :key="index" class="item-row">
              <span class="item-qty">{{ item.quantity || 1 }}x</span>
              <div class="item-details">
                <span class="item-name">{{ item.name }}</span>
                <!-- Customizations: Size, Addons, Removed -->
                <div v-if="item.customization" class="item-modifiers">
                  <!-- Size -->
                  <div v-if="item.customization.Größe || item.customization['Größe ']" class="modifier-item">
                    <span class="modifier-name size-badge">{{ item.customization.Größe || item.customization['Größe '] }}</span>
                  </div>
                  <!-- Added Addons -->
                  <div v-if="item.customization.addedAddons && item.customization.addedAddons.length > 0" class="addons-list">
                    <div v-for="(addon, aIdx) in item.customization.addedAddons" :key="aIdx" class="modifier-item">
                      <span class="modifier-name">+ {{ addon.name }}<span v-if="addon.quantity > 1"> ({{ addon.quantity }}x)</span></span>
                      <span v-if="addon.price && addon.price > 0" class="modifier-price">€{{ addon.price.toFixed(2) }}</span>
                    </div>
                  </div>
                  <!-- Removed Ingredients -->
                  <div v-if="item.customization.removedIngredients && item.customization.removedIngredients.length > 0" class="removed-list">
                    <div v-for="(ing, rIdx) in item.customization.removedIngredients" :key="rIdx" class="modifier-item removed">
                      <span class="modifier-name">- {{ ing.name || ing }}</span>
                    </div>
                  </div>
                </div>
                <span v-if="item.notes" class="item-notes">{{ item.notes }}</span>
              </div>
              <span class="item-price">€{{ (item.customPrice || item.price || 0).toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Order Total -->
        <div class="order-total">
          <span>Gesamt</span>
          <span class="total-amount">€{{ orderTotal.toFixed(2) }}</span>
        </div>

        <!-- Prep Time Section (always visible) -->
        <div v-if="!showDenyReasons" class="prep-time-section">
          <div class="section-title">
            <i class="fas fa-clock"></i>
            <span>Zubereitungszeit</span>
          </div>
          <div class="prep-time-control">
            <button class="time-btn minus" @click="decreasePrepTime" :disabled="prepTime <= 5">
              <i class="fas fa-minus"></i>
            </button>
            <div class="time-display">
              <span class="time-value">{{ prepTime }}</span>
              <span class="time-unit">min</span>
            </div>
            <button class="time-btn plus" @click="increasePrepTime">
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>

        <!-- Deny Reason Section -->
        <div v-if="showDenyReasons" class="deny-section">
          <div class="section-title">
            <i class="fas fa-times-circle"></i>
            <span>Ablehnungsgrund</span>
          </div>
          <div class="deny-reasons">
            <button
              v-for="reason in denyReasons"
              :key="reason.id"
              :class="['reason-btn', { active: selectedDenyReason === reason.id }]"
              @click="selectedDenyReason = reason.id"
            >
              {{ reason.label }}
            </button>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <!-- Main State - Accept/Deny -->
          <template v-if="!showDenyReasons">
            <button class="deny-btn" @click="showDenyReasons = true">
              <i class="fas fa-times"></i>
              Ablehnen
            </button>
            <button class="accept-btn" @click="confirmAccept">
              <i class="fas fa-check"></i>
              Annehmen ({{ prepTime }} min)
            </button>
          </template>

          <!-- Deny State -->
          <template v-else>
            <button class="back-btn" @click="showDenyReasons = false; selectedDenyReason = null">
              <i class="fas fa-arrow-left"></i>
              Zurück
            </button>
            <button
              class="confirm-deny-btn"
              :disabled="!selectedDenyReason"
              @click="confirmDeny"
            >
              <i class="fas fa-times"></i>
              Ablehnung bestätigen
            </button>
          </template>
        </div>

        <!-- Preview Mode Indicator -->
        <div v-if="isPreview" class="preview-indicator">
          <i class="fas fa-eye"></i>
          Vorschau-Modus
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'UberOrderModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: () => ({})
    },
    isPreview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showDenyReasons: false,
      showCustomerInfo: false,
      selectedDenyReason: null,
      prepTime: 15, // Default 15 minutes
      denyReasons: [
        { id: 'items_unavailable', label: 'Artikel nicht verfügbar' },
        { id: 'kitchen_busy', label: 'Küche zu beschäftigt' },
        { id: 'closing_soon', label: 'Schließen bald' },
        { id: 'other', label: 'Anderer Grund' }
      ],
      mockItems: [
        { name: 'Best Burger', quantity: 1, price: 12.90, notes: 'Ohne Zwiebeln' },
        { name: 'Pommes Frites', quantity: 2, price: 4.50 },
        { name: 'Cola 0.5L', quantity: 1, price: 3.00 }
      ]
    };
  },
  computed: {
    itemCount() {
      const items = this.order.items || this.mockItems;
      return items.reduce((sum, item) => sum + (item.quantity || 1), 0);
    },
    orderTotal() {
      const items = this.order.items || this.mockItems;
      return items.reduce((sum, item) => sum + ((item.customPrice || item.price || 0) * (item.quantity || 1)), 0);
    }
  },
  methods: {
    handleOverlayClick() {
      if (this.isPreview) {
        this.$emit('close');
      }
      // Real orders cannot be dismissed by clicking outside
    },
    increasePrepTime() {
      this.prepTime += 5;
    },
    decreasePrepTime() {
      if (this.prepTime > 5) {
        this.prepTime -= 5;
      }
    },
    confirmAccept() {
      if (this.isPreview) {
        alert(`Vorschau: Bestellung angenommen mit ${this.prepTime} Minuten Zubereitungszeit`);
        this.resetState();
        this.$emit('close');
        return;
      }
      this.$emit('accept', {
        orderId: this.order.uberOrderId,
        prepTime: this.prepTime
      });
      this.resetState();
    },
    confirmDeny() {
      if (this.isPreview) {
        const reason = this.denyReasons.find(r => r.id === this.selectedDenyReason);
        alert(`Vorschau: Bestellung abgelehnt - ${reason?.label}`);
        this.resetState();
        this.$emit('close');
        return;
      }
      this.$emit('deny', {
        orderId: this.order.uberOrderId,
        reason: this.selectedDenyReason
      });
      this.resetState();
    },
    resetState() {
      this.showDenyReasons = false;
      this.showCustomerInfo = false;
      this.selectedDenyReason = null;
      this.prepTime = 15;
    },
    callCustomer() {
      const phone = this.order.customer?.phone || '+49123456789';
      window.location.href = `tel:${phone.replace(/\s/g, '')}`;
    }
  },
  watch: {
    isOpen(newVal) {
      if (!newVal) {
        this.resetState();
      }
    }
  }
};
</script>

<style scoped>
/* Modal Transitions */
.uber-modal-enter-active,
.uber-modal-leave-active {
  transition: opacity 0.3s ease;
}

.uber-modal-enter-active .uber-modal,
.uber-modal-leave-active .uber-modal {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.uber-modal-enter,
.uber-modal-leave-to {
  opacity: 0;
}

.uber-modal-enter .uber-modal,
.uber-modal-leave-to .uber-modal {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}

/* Overlay */
.uber-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

/* Modal Container */
.uber-modal {
  background: var(--ods-surface);
  border-radius: var(--ods-radius-lg);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

/* Uber Header */
.uber-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f7f7f7;
  border-radius: var(--ods-radius-lg) var(--ods-radius-lg) 0 0;
}

.uber-logo {
  display: flex;
  align-items: center;
}

.uber-logo img {
  height: 32px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-order-id {
  font-size: 24px;
  font-weight: 700;
  color: #142328;
}

.call-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #30d158;
  border: none;
  border-radius: 50%;
  color: var(--ods-ink);
  font-size: 14px;
  cursor: pointer;
}

.call-btn:hover {
  background: #34c759;
}

/* Customer Info Popup */
.customer-info-popup {
  margin: 16px 20px;
  padding: 12px 16px;
  background: var(--ods-surface);
  border-radius: var(--ods-radius);
}

.customer-info-popup .info-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--ods-line);
}

.customer-info-popup .info-row:last-child {
  border-bottom: none;
}

.customer-info-popup .info-row i {
  width: 18px;
  color: var(--ods-ink-2);
  font-size: 13px;
}

.customer-info-popup .info-row span {
  color: var(--ods-ink);
  font-size: 14px;
}

.customer-info-popup .info-row.phone {
  cursor: pointer;
}

.customer-info-popup .info-row.phone span {
  color: var(--ods-accent-ink);
}

.customer-info-popup .pin {
  font-weight: 600;
  font-size: 16px !important;
  color: var(--ods-accent-ink) !important;
}

.customer-info-popup .info-row.notes span {
  color: var(--ods-ink-2);
}

.new-order-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #06C167;
  padding: 8px 14px;
  border-radius: var(--ods-radius-lg);
  font-size: 13px;
  font-weight: 600;
  color: var(--ods-ink);
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background: var(--ods-surface);
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}

/* Order Info */
.order-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--ods-line);
}

.order-id .label {
  font-size: 12px;
  color: var(--ods-ink-3);
  display: block;
  margin-bottom: 4px;
}

.order-id .value {
  font-size: 24px;
  font-weight: 700;
  color: #06C167;
}

.customer-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--ods-ink-2);
  font-size: 14px;
}

.customer-info i {
  color: var(--ods-ink-3);
}

/* Items Section */
.items-section {
  padding: 16px 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ods-ink-2);
  margin-bottom: 12px;
}

.section-title i {
  color: var(--ods-ink-2);
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: var(--ods-surface);
  border-radius: var(--ods-radius);
}

.item-qty {
  font-size: 15px;
  font-weight: 600;
  color: var(--ods-ink);
  min-width: 28px;
}

.item-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name {
  font-size: 15px;
  font-weight: 500;
  color: var(--ods-ink);
}

.item-notes {
  font-size: 12px;
  color: var(--ods-accent-ink);
}

/* Modifiers (Size, Extras, Ingredients) */
.item-modifiers {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modifier-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background: rgba(48, 209, 88, 0.1);
  border-radius: var(--ods-radius);
}

.modifier-item.removed {
  background: rgba(255, 69, 58, 0.1);
}

.modifier-item.removed .modifier-name {
  color: #ff453a;
}

.modifier-name {
  font-size: 13px;
  color: #30d158;
  font-weight: 500;
}

.modifier-name.size-badge {
  color: var(--ods-accent-ink);
  background: rgba(10, 132, 255, 0.15);
  padding: 2px 8px;
  border-radius: 4px;
}

.modifier-price {
  font-size: 12px;
  color: var(--ods-ink-2);
  margin-left: 8px;
}

.addons-list,
.removed-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-price {
  font-size: 14px;
  font-weight: 500;
  color: var(--ods-ink-2);
}

/* Order Total */
.order-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  margin: 0 20px 16px;
  background: var(--ods-surface);
  border-radius: var(--ods-radius);
}

.order-total span:first-child {
  font-size: 14px;
  font-weight: 500;
  color: var(--ods-ink-2);
}

.total-amount {
  font-size: 22px;
  font-weight: 700;
  color: #30d158;
}

/* Prep Time Section */
.prep-time-section {
  padding: 0 20px 16px;
}

.prep-time-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 16px;
  background: var(--ods-surface);
  border-radius: var(--ods-radius);
}

.time-btn {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: var(--ods-surface-2);
  color: var(--ods-ink);
  font-size: 18px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.time-btn:hover:not(:disabled) {
  background: #48484a;
}

.time-btn:active:not(:disabled) {
  background: #545456;
}

.time-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.time-btn.plus {
  background: #30d158;
  color: var(--ods-ink);
}

.time-btn.plus:hover {
  background: #34c759;
}

.time-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 70px;
}

.time-value {
  font-size: 40px;
  font-weight: 700;
  color: var(--ods-ink);
  line-height: 1;
}

.time-unit {
  font-size: 13px;
  color: var(--ods-ink-2);
  margin-top: 2px;
}

/* Deny Section */
.deny-section {
  padding: 0 20px 16px;
}

.deny-reasons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reason-btn {
  padding: 14px 16px;
  background: var(--ods-surface);
  border: none;
  border-radius: var(--ods-radius);
  color: var(--ods-ink);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
}

.reason-btn:hover {
  background: var(--ods-surface-2);
}

.reason-btn.active {
  background: #ff453a;
  color: var(--ods-ink);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 10px;
  padding: 0 20px 20px;
}

.action-buttons button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: var(--ods-radius);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.deny-btn {
  background: var(--ods-surface);
  color: #ff453a;
}

.deny-btn:hover {
  background: var(--ods-surface-2);
}

.accept-btn {
  background: #30d158;
  color: var(--ods-ink);
}

.accept-btn:hover {
  background: #34c759;
}

.back-btn {
  background: var(--ods-surface);
  color: var(--ods-ink);
}

.back-btn:hover {
  background: var(--ods-surface-2);
}

.confirm-btn {
  background: #30d158;
  color: var(--ods-ink);
}

.confirm-btn:disabled {
  background: var(--ods-surface-2);
  color: var(--ods-line);
  cursor: not-allowed;
}

.confirm-btn:not(:disabled):hover {
  background: #34c759;
}

.confirm-deny-btn {
  background: #ff453a;
  color: var(--ods-ink);
}

.confirm-deny-btn:disabled {
  background: var(--ods-surface-2);
  color: var(--ods-line);
  cursor: not-allowed;
}

.confirm-deny-btn:not(:disabled):hover {
  background: #ff5147;
}

/* Preview Indicator */
.preview-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  background: var(--ods-surface);
  color: var(--ods-ink-2);
  font-size: 12px;
  font-weight: 500;
  border-radius: 0 0 var(--ods-radius-lg) var(--ods-radius-lg);
}

/* Scrollbar */
.uber-modal::-webkit-scrollbar {
  width: 6px;
}

.uber-modal::-webkit-scrollbar-track {
  background: transparent;
}

.uber-modal::-webkit-scrollbar-thumb {
  background: var(--ods-surface-3);
  border-radius: 3px;
}

.uber-modal::-webkit-scrollbar-thumb:hover {
  background: var(--ods-surface-3);
}
</style>
